import { Building2, Info, Plus, User } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { createWorkspace } from '@/client/sdk.gen'
import type { WorkspaceCreateRequest, WorkspaceTypeEnum } from '@/client/types.gen'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth, useWorkspace } from '@/context'
import { useProductTourOptional } from '@/context/ProductTourContext'
import { refreshMembershipRights } from '@/hooks/useMembershipRights'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import {
	captureUnexpectedError,
	extractErrorMessage,
	isErrorHandledByInterceptor,
} from '@/utils/error'

type WorkspaceKind = 'personal' | 'company'

interface NewWorkspaceFormValues {
	name: string
	kind: WorkspaceKind
}

interface WorkspaceSwitcherProps {
	className?: string
	/**
	 * Navigate on select:
	 * When true (default), switches workspace and navigates to /projects.
	 * Set to false when embedding in pages that should stay put (e.g. Settings).
	 */
	navigateOnSelect?: boolean
}

function WorkspaceSwitcher({ className = '', navigateOnSelect = true }: WorkspaceSwitcherProps) {
	const { t } = useTranslation()
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false)
	const [newWorkspaceName, setNewWorkspaceName] = useState('')
	const [newWorkspaceKind, setNewWorkspaceKind] = useState<WorkspaceKind>('personal')
	const nameInputRef = useRef<HTMLInputElement | null>(null)
	const { user, checkAuth } = useAuth()
	const { workspaces, selectedWorkspaceId, setSelectedWorkspaceId } = useWorkspace()
	const navigate = useNavigate()
	const productTour = useProductTourOptional()
	const isTourWorkspaceSwitcherLocked = productTour?.isTourActive

	const currentWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId) || workspaces[0]

	/**
	 * Get the appropriate icon for a workspace based on its type
	 * @param workspaceType - The workspace type ('personal' or 'company')
	 * @returns The icon component to render
	 */
	const getWorkspaceIcon = (workspaceType?: string) => {
		if (workspaceType === 'company') {
			return <Building2 className="h-4 w-4" />
		}
		return <User className="h-4 w-4" />
	}

	const handleSelect = (workspaceId: string) => {
		if (workspaceId === '__create__') {
			setIsCreateModalOpen(true)
			return
		}

		setSelectedWorkspaceId(workspaceId)

		if (navigateOnSelect) {
			navigate('/projects', { replace: true })
		}
	}

	/**
	 * Create a workspace using the form data
	 * @param values - The form data for the new workspace
	 */
	const handleWorkspaceCreation = async (values: NewWorkspaceFormValues) => {
		if (!user?.id) {
			toast.error(t('common.workspaceSwitcher.createWorkspace.errors.noUser'))
			return
		}

		if (isCreatingWorkspace) {
			return
		}

		setIsCreatingWorkspace(true)

		try {
			// Map kind to workspace_type enum value
			const workspaceType: WorkspaceTypeEnum = values.kind === 'company' ? 'company' : 'personal'

			// Build the request body
			const requestBody: WorkspaceCreateRequest = {
				name: values.name,
				workspace_type: workspaceType,
			}

			// Use company ID from auth context if the workspace type is company
			if (values.kind === 'company') {
				if (!user.company_id) {
					captureUnexpectedError(new Error('Missing company_id for company workspace creation'))
					toast.error(t('common.workspaceSwitcher.createWorkspace.errors.failedToFetchCompany'))
					setIsCreateModalOpen(false)
					setNewWorkspaceName('')
					setNewWorkspaceKind('personal')
					return
				}
				requestBody.company_id = user.company_id
			} else {
				// For personal workspaces, ensure company_id is not sent
				requestBody.company_id = null
			}

			// Create the workspace using the generated client
			const response = await createWorkspace({
				body: requestBody,
			})

			if (response.data?.success) {
				const newWorkspaceId = response.data.workspace_id ?? response.data.workspace?.id ?? null

				toast.success(t('common.workspaceSwitcher.createWorkspace.success.workspaceCreated'))
				setIsCreateModalOpen(false)
				setNewWorkspaceName('')
				setNewWorkspaceKind('personal')

				// Refresh user data to get updated workspaces list
				// The workspace context will automatically update when user.workspaces changes
				await checkAuth()
				// Membership rights are cached by user id; refresh so canCreateProject
				// includes the new workspace without a full page reload
				await refreshMembershipRights(user.id)

				if (newWorkspaceId) {
					setSelectedWorkspaceId(newWorkspaceId)
				}

				if (navigateOnSelect) {
					navigate('/projects', { replace: true })
				}
			} else {
				toast.error(
					response.data?.message || t('common.workspaceSwitcher.createWorkspace.errors.failedToCreate')
				)
			}
		} catch (error) {
			// Skip showing toast if error was already handled by interceptor
			if (isErrorHandledByInterceptor(error)) {
				return
			}

			const detail = extractErrorMessage(
				error,
				t('common.workspaceSwitcher.createWorkspace.errors.failedToCreate')
			)
			toast.error(detail)
		} finally {
			setIsCreatingWorkspace(false)
		}
	}

	return (
		<div className={className} data-tour={TOUR.workspaceSwitcher}>
			<Select
				value={selectedWorkspaceId ?? ''}
				onValueChange={handleSelect}
				disabled={isTourWorkspaceSwitcherLocked}
			>
				<SelectTrigger
					disabled={isTourWorkspaceSwitcherLocked}
					className="max-w-[10rem] sm:max-w-[8rem] md:max-w-[18rem] lg:max-w-xs hover:scale-105 transition-transform duration-200 ease-in-out disabled:hover:scale-100"
				>
					<SelectValue
						placeholder={
							<span className="flex items-center gap-2">
								<User className="h-4 w-4" />

								<span className="hidden sm:inline">{t('common.workspaceSwitcher.selectWorkspace')}</span>
							</span>
						}
					>
						<span className="flex items-center gap-2 min-w-0">
							{currentWorkspace ? (
								getWorkspaceIcon(currentWorkspace.workspace_type)
							) : (
								<User className="h-4 w-4" />
							)}

							<span className="hidden sm:inline truncate max-w-[7rem] sm:max-w-[10rem] md:max-w-[12rem]">
								{currentWorkspace?.name ?? t('common.workspaceSwitcher.selectWorkspace')}
							</span>
						</span>
					</SelectValue>
				</SelectTrigger>

				<SelectContent align="end" className="max-h-52 mm-scrollbar" position="popper">
					{workspaces.map((workspace) => (
						<SelectItem key={workspace.id} value={workspace.id}>
							<span className="flex items-center gap-2">
								{getWorkspaceIcon(workspace.workspace_type)}

								<span>{workspace.name}</span>
							</span>
						</SelectItem>
					))}

					<SelectItem value="__create__" className="text-purple-600 font-medium">
						<span className="flex items-center gap-2">
							<Plus className="h-4 w-4" />

							<span>{t('common.workspaceSwitcher.newWorkspace')}</span>
						</span>
					</SelectItem>
				</SelectContent>
			</Select>

			<AlertDialog
				open={isCreateModalOpen}
				onOpenChange={(open) => {
					if (isCreatingWorkspace) {
						return
					}

					setIsCreateModalOpen(open)
				}}
			>
				<AlertDialogContent
					onOpenAutoFocus={(event) => {
						event.preventDefault()
						nameInputRef.current?.focus()
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('common.workspaceSwitcher.createWorkspace.title')}</AlertDialogTitle>

						<AlertDialogDescription>
							{t('common.workspaceSwitcher.createWorkspace.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<form
						onSubmit={(event) => {
							event.preventDefault()
							handleWorkspaceCreation({
								name: newWorkspaceName,
								kind: newWorkspaceKind,
							})
						}}
						className="flex flex-col gap-4"
					>
						<div className="flex flex-col gap-2">
							<Label htmlFor="workspace-name">
								{t('common.workspaceSwitcher.createWorkspace.nameLabel')}
							</Label>

							<Input
								ref={nameInputRef}
								id="workspace-name"
								type="text"
								value={newWorkspaceName}
								onChange={(event) => setNewWorkspaceName(event.target.value)}
								placeholder={t('common.workspaceSwitcher.createWorkspace.namePlaceholder')}
								disabled={isCreatingWorkspace}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<Label className="flex items-center gap-2">
								<span>{t('common.workspaceSwitcher.createWorkspace.typeLabel')}</span>

								<Tooltip>
									<TooltipTrigger asChild>
										<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
									</TooltipTrigger>
									<TooltipContent>
										<p>{t('common.workspaceSwitcher.createWorkspace.typeTooltip')}</p>
									</TooltipContent>
								</Tooltip>
							</Label>

							<div className="flex gap-4">
								<label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
									<input
										type="radio"
										name="workspace-type"
										value="personal"
										checked={newWorkspaceKind === 'personal'}
										onChange={() => setNewWorkspaceKind('personal')}
										disabled={isCreatingWorkspace}
										className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
									/>
									<span>{t('common.workspaceSwitcher.createWorkspace.personal')}</span>
								</label>

								<label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
									<input
										type="radio"
										name="workspace-type"
										value="company"
										checked={newWorkspaceKind === 'company'}
										onChange={() => setNewWorkspaceKind('company')}
										disabled={isCreatingWorkspace}
										className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
									/>
									<span>{t('common.workspaceSwitcher.createWorkspace.company')}</span>
								</label>
							</div>
						</div>

						<AlertDialogFooter>
							<AlertDialogCancel
								className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
								type="button"
								disabled={isCreatingWorkspace}
							>
								{t('common.workspaceSwitcher.createWorkspace.closeButton')}
							</AlertDialogCancel>

							<Button
								className="hover:scale-105 active:scale-95 transition-transform duration-200 ease-in-out"
								type="submit"
								disabled={isCreatingWorkspace}
							>
								{isCreatingWorkspace && <Spinner className="mr-2" />}
								{t('common.workspaceSwitcher.createWorkspace.createButton')}
							</Button>
						</AlertDialogFooter>
					</form>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

export { WorkspaceSwitcher }
